import { Controller } from '@hotwired/stimulus';

/**
 * Assignment List Controller
 *
 * Manages the assignment modal: client-side search filtering,
 * select-all checkbox, checked row highlighting, and dynamic count.
 *
 * Targets:
 * - search: The search input field
 * - row: Each collaborator row (filterable)
 * - checkbox: Each collaborator checkbox
 * - selectAll: The select-all header checkbox
 * - count: The dynamic count text element
 *
 * @example
 * <div data-controller="assignment-list">
 *   <input data-assignment-list-target="search"
 *          data-action="input->assignment-list#filter">
 *   <input type="checkbox" data-assignment-list-target="selectAll"
 *          data-action="change->assignment-list#toggleAll">
 *   <li data-assignment-list-target="row">
 *     <input type="checkbox" data-assignment-list-target="checkbox"
 *            data-action="change->assignment-list#updateCount">
 *   </li>
 *   <span data-assignment-list-target="count">0</span>
 * </div>
 */
export default class extends Controller {
  static targets = ['search', 'row', 'checkbox', 'selectAll', 'count', 'label'];

  connect() {
    this.#updateCount();
    this.#updateSelectAll();
  }

  filter() {
    const query = this.searchTarget.value.toLowerCase().trim();

    this.rowTargets.forEach((row) => {
      const text = row.dataset.searchText || row.textContent.toLowerCase();
      row.classList.toggle('hidden', query.length > 0 && !text.includes(query));
    });

    this.#updateSelectAll();
  }

  toggleAll() {
    const checked = this.selectAllTarget.checked;
    this.#visibleCheckboxes().forEach((cb) => {
      cb.checked = checked;
      this.#highlightRow(cb);
    });
    this.#updateCount();
  }

  updateCount(event) {
    const checkbox = event.target;
    this.#highlightRow(checkbox);
    this.#updateCount();
    this.#updateSelectAll();
  }

  // Private

  #visibleCheckboxes() {
    return this.checkboxTargets.filter(
      (cb) => !cb.closest('[data-assignment-list-target="row"]')?.classList.contains('hidden')
    );
  }

  #updateCount() {
    const checked = this.checkboxTargets.filter((cb) => cb.checked).length;
    if (this.hasCountTarget) {
      this.countTarget.textContent = checked;
    }
  }

  #updateSelectAll() {
    if (!this.hasSelectAllTarget) return;

    const visible = this.#visibleCheckboxes();
    const checkedVisible = visible.filter((cb) => cb.checked).length;

    this.selectAllTarget.checked = visible.length > 0 && checkedVisible === visible.length;
    this.selectAllTarget.indeterminate = checkedVisible > 0 && checkedVisible < visible.length;
  }

  #highlightRow(checkbox) {
    const label = checkbox.closest('[data-assignment-list-target="label"]');
    if (label) {
      label.classList.toggle('bg-primary-50', checkbox.checked);
    }
  }
}
